// ACT College Score Reporting Service CSV (Effective Apr 1, 2025). Detect by the presence of the
// ACT header family on the first line: name columns (L_Name + F_Name), the three required section
// scores (Eng + Mth + Rdg), and the State-Assigned Student ID (ID_StateAssign). The combination is
// specific to the ACT layout, so it will not misfire on SAT, CCMR, roster, or accelerated-testers
// CSVs. A filename like `ACT-<src>-<org>-<MMDDYYYY>-<HHMMSS>.csv` is accepted as a fallback signal.
const REQUIRED_HEADERS = ['L_Name', 'F_Name', 'Eng', 'Mth', 'Rdg', 'ID_StateAssign'];

// The portal's "Roster View: The ACT (All Data)" export is the other CSV districts hand us. Its
// header sits below a title block rather than on the first line, and it carries no state id, so
// it needs its own signature. The ACT-prefixed score columns make the combination unambiguous.
const REQUIRED_ROSTER_VIEW_HEADERS = [
  'ACT ID',
  'Last Name',
  'First Name',
  'ACT composite score',
  'ACT English score',
  'ACT math score',
  'ACT reading score',
];

// A title row and a filter note precede the Roster View header, so scan a bounded window of
// leading lines instead of assuming the first non-blank one is the header.
const HEADER_SEARCH_LINES = 10;

const splitHeaders = (headerLine) =>
  // ACT ships every field double-quoted; strip quotes and surrounding whitespace before matching.
  headerLine.split(',').map((h) => h.replace(/"/g, '').trim());

const hasAllHeaders = (headerLine, required) => {
  const headers = splitHeaders(headerLine);
  return required.every((needed) => headers.includes(needed));
};

const validateFile = (text) =>
  new Promise((resolve) => {
    const lines = text.split(/\r\n?|\n/).filter((line) => line && line.trim().length > 0);
    if (!lines.length) {
      resolve(false);
      return;
    }
    const scoreReportingMatched = hasAllHeaders(lines[0], REQUIRED_HEADERS);
    const rosterViewMatched = lines
      .slice(0, HEADER_SEARCH_LINES)
      .some((line) => hasAllHeaders(line, REQUIRED_ROSTER_VIEW_HEADERS));
    resolve(scoreReportingMatched || rosterViewMatched);
  });

const isACT = (file) =>
  new Promise((resolve) => {
    if (file.type !== 'application/vnd.ms-excel' && file.type !== 'text/csv') {
      resolve(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const headerMatched = await validateFile(reader.result);
      const filenameMatched = /^ACT-/i.test(file.name || '');
      resolve(headerMatched || filenameMatched);
    };
    reader.readAsText(file);
  });

export default isACT;
